import { ReactElement, useRef, useMemo } from 'react';
import { Polygon } from 'geojson';
import mapboxgl from 'mapbox-gl';
import Map, {
  MapRef,
  FullscreenControl,
  NavigationControl,
  Source,
  Layer,
} from 'react-map-gl';
import { compact } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { usePopupRequest } from '~hooks/usePopupRequest';
import { getItemInfo } from '~services/item';
import {
  ShowTripViewControl,
  ModeSateliteControl,
} from '~components/MapCustomControl';
import MarkerDeliveryType from '~components/MarkerDeliveryType';
import DialogDetailsOrder from '~components/DialogDetailsOrder';
import { TOKEN } from '~utils/mapbox';
import { controlLocale } from '~constants/mapControlLocale';

import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../../Context';
import { useDashboardVariantV2CarrierContentCollectTrackingContext } from '../Context';
// eslint-disable-next-line max-len
import DashboardVariantV2CarrierContentInWarehouseTabMapPopup from '../../../../InWarehouse/Components/Map/Popup';

import { getZonePolygonFeature } from './utils';
import {
  DashboardVariantV2CarrierContentCollectTabTrackingMapContainer,
  DashboardVariantV2CarrierContentCollectTabTrackingMapLoading,
} from './styles';

const DashboardVariantV2CarrierContentCollectTabTrackingMap =
  (): ReactElement => {
    const {
      dialogs: {
        detailsOrder: {
          data: selectedDataForDetailsOrder,
          open: openDialogDetailsOrder,
          handleOpen: handleOpenDialogDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
      },
    } = useDashboardVariantV2CarrierContentCollectTabContext();

    const {
      tabsData: { loading: loadingTabData },
      mapData: { loading: loadingMapData, items, logisticZones },
    } = useDashboardVariantV2CarrierContentCollectTrackingContext();

    const mapRef = useRef<Nullable<MapRef>>(null);

    const hasData = useMemo(() => {
      const dataCompact = compact([items.length, logisticZones.length]);

      return dataCompact.length > 0;
    }, [items.length, logisticZones.length]);

    const bounds = useMemo(() => {
      const currentBounds = new mapboxgl.LngLatBounds();

      if (items.length > 0) {
        items.forEach((item) => {
          if (item.coordinates.length > 1) {
            currentBounds.extend(item.coordinates);
          }
        });
      }

      if (logisticZones.length > 0) {
        logisticZones.forEach((logisticZone) => {
          if (logisticZone.geometryAreaJson) {
            const zonePolygon: Polygon = JSON.parse(
              logisticZone.geometryAreaJson,
            );

            zonePolygon.coordinates[0].forEach((coord) => {
              const polygonCoords = new mapboxgl.LngLat(coord[0], coord[1]);

              currentBounds.extend(polygonCoords);
            });
          }
        });
      }

      if (!currentBounds.isEmpty()) {
        mapRef.current?.fitBounds(currentBounds, { padding: 100, maxZoom: 12 });
      }

      return currentBounds;
    }, [items, logisticZones]);

    const {
      handleOpenPopup: handleOpenPopupMarkerItem,
      handleClosePopup: handleClosePopupMarkerItem,
      ...restPopupOrder
    } = usePopupRequest({
      request: getItemInfo,
    });

    return (
      <DashboardVariantV2CarrierContentCollectTabTrackingMapContainer>
        {(loadingMapData || loadingTabData) && (
          <DashboardVariantV2CarrierContentCollectTabTrackingMapLoading />
        )}

        {hasData && (
          <Map
            ref={mapRef}
            mapboxAccessToken={TOKEN}
            mapStyle="mapbox://styles/mapbox/light-v10"
            initialViewState={{
              bounds,
              fitBoundsOptions: {
                padding: 100,
                maxZoom: 12,
              },
            }}
            attributionControl={false}
            locale={controlLocale}
          >
            <FullscreenControl position="bottom-left" />
            <NavigationControl position="bottom-left" showCompass={false} />
            {bounds && (
              <ShowTripViewControl
                position="bottom-left"
                currentBounds={bounds}
              />
            )}
            <ModeSateliteControl position="bottom-left" />

            {items.map((item, itemIndex) => (
              <MarkerDeliveryType
                key={`marker-item-${item.itemId}-${itemIndex}`}
                sourceImportTypeId={item.sourceImportTypeId}
                longitude={item.coordinates[0]}
                latitude={item.coordinates[1]}
                onClick={(event) =>
                  handleOpenPopupMarkerItem(
                    event,
                    item.itemId,
                    item.coordinates,
                  )
                }
                anchor="bottom"
                style={{ cursor: 'pointer' }}
              />
            ))}

            <DashboardVariantV2CarrierContentInWarehouseTabMapPopup
              {...restPopupOrder}
              handleClosePopup={handleClosePopupMarkerItem}
              isItem
              onShowDetailOrder={handleOpenDialogDetailsOrder}
            />

            {logisticZones.map((zone) => (
              <Source
                key={zone.zoneId}
                id={`zone-${zone.zoneId}`}
                type="geojson"
                data={getZonePolygonFeature(zone)}
              >
                <Layer
                  id={`zone-${zone.zoneId}-fill`}
                  type="fill"
                  paint={{
                    'fill-color': ['get', 'currentColor'],
                    'fill-outline-color': ['get', 'currentColor'],
                    'fill-opacity': 0.1,
                  }}
                />

                <Layer
                  id={`zone-${zone.zoneId}-line`}
                  type="line"
                  layout={{
                    'line-join': 'round',
                    'line-cap': 'round',
                  }}
                  paint={{
                    'line-color': ['get', 'currentColor'],
                    'line-width': 2,
                  }}
                />

                <Layer
                  id={`zone-${zone.zoneId}-label`}
                  type="symbol"
                  layout={{
                    'text-field': ['get', 'label'],
                    'text-size': 12,
                    'text-offset': [0, 1.25],
                    'text-anchor': 'center',
                  }}
                  paint={{
                    'text-color': ['get', 'currentColor'],
                  }}
                />
              </Source>
            ))}
          </Map>
        )}

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle
            {...selectedDataForDetailsOrder}
          />
        )}
      </DashboardVariantV2CarrierContentCollectTabTrackingMapContainer>
    );
  };

export default DashboardVariantV2CarrierContentCollectTabTrackingMap;
